import React from "react";
import Modal from "../../../component/ui/modal";
import TextInput from "../../../component/element/forms/TextInput";
import TextBox from "../../../component/element/forms/TextBox";
import SelectOption from "../../../component/element/forms/SelectOption";
import DocumentUpload from "../../../component/modals/upload/DocumentUpload";
import { ISimpleData } from "../../../store/types/misc";
import { ILawyer } from "../../../store/types/lawyers";
import useLawyerForm from "../hooks/useLawyerForm";

type ILawyerFormModal = {
  open: boolean;
  lawyer?: ILawyer | null;
  onClose: () => void;
};

const statusOptions: ISimpleData[] = [
  { id: "pending", name: "Pending" },
  { id: "approved", name: "Approved" },
];

const LawyerFormModal = ({
  open,
  lawyer = null,
  onClose,
}: ILawyerFormModal) => {
  const {
    values,
    errors,
    cvPreview,
    loading,
    onChange,
    setFieldValue,
    onCvChange,
    submit,
  } = useLawyerForm(open, lawyer);

  const handleSubmit = async () => {
    if (loading) return;
    if (await submit()) onClose();
  };

  const fieldStatus = (field: keyof typeof errors) =>
    errors[field] ? "error: this field is required" : "";

  return (
    <Modal
      title={lawyer ? "Edit Lawyer" : "Create Lawyer"}
      open={open}
      onClose={onClose}
      onOkClick={handleSubmit}
      isLoading={loading}
      variant="large"
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <TextInput
            label="Full name"
            name="name"
            type="text"
            placeholder="Enter full name"
            value={values.name}
            onChange={onChange}
            status={fieldStatus("name")}
            required
          />
          {errors.name && <FieldError>{errors.name}</FieldError>}
        </div>
        <div>
          <TextInput
            label="Email"
            name="email"
            type="email"
            placeholder="name@example.com"
            value={values.email}
            onChange={onChange}
            status={fieldStatus("email")}
            required
          />
          {errors.email && <FieldError>{errors.email}</FieldError>}
        </div>
        <div>
          <TextInput
            label="Username"
            name="username"
            type="text"
            placeholder="Enter username"
            value={values.username}
            onChange={onChange}
            status={fieldStatus("username")}
            required
          />
          {errors.username && <FieldError>{errors.username}</FieldError>}
        </div>
        <div>
          <TextInput
            label={lawyer ? "New password (optional)" : "Password"}
            name="password"
            type="password"
            placeholder={
              lawyer ? "Leave blank to keep current" : "At least 8 characters"
            }
            value={values.password}
            onChange={onChange}
            status={fieldStatus("password")}
            required={!lawyer}
          />
          {errors.password && <FieldError>{errors.password}</FieldError>}
        </div>
        <div>
          <TextInput
            label="Phone number"
            name="phone_number"
            type="tel"
            placeholder="Enter phone number"
            value={values.phone_number}
            onChange={onChange}
            status={fieldStatus("phone_number")}
            required
          />
          {errors.phone_number && (
            <FieldError>{errors.phone_number}</FieldError>
          )}
        </div>
        <div>
          <TextInput
            label="Bar number"
            name="bar_number"
            type="text"
            placeholder="Enter bar number"
            value={values.bar_number}
            onChange={onChange}
            status={fieldStatus("bar_number")}
            required
          />
          {errors.bar_number && <FieldError>{errors.bar_number}</FieldError>}
        </div>
        <div>
          <TextInput
            label="Specialization"
            name="specialization"
            type="text"
            placeholder="e.g. Corporate law"
            value={values.specialization}
            onChange={onChange}
            status={fieldStatus("specialization")}
            required
          />
          {errors.specialization && (
            <FieldError>{errors.specialization}</FieldError>
          )}
        </div>
        <div>
          <TextInput
            label="Years of experience"
            name="years_experience"
            type="number"
            placeholder="0"
            value={values.years_experience}
            onChange={onChange}
            status={fieldStatus("years_experience")}
            required
          />
          {errors.years_experience && (
            <FieldError>{errors.years_experience}</FieldError>
          )}
        </div>
        <div className="md:col-span-2">
          <TextInput
            label="Countries"
            name="countries"
            type="text"
            placeholder="Separate countries with commas"
            value={values.countries}
            onChange={onChange}
            status={fieldStatus("countries")}
            required
          />
          {errors.countries && <FieldError>{errors.countries}</FieldError>}
        </div>
        <div className="md:col-span-2">
          <TextBox
            label="Professional biography"
            name="bio"
            placeholder="Describe the lawyer's professional background"
            value={values.bio}
            onChange={(_, value) => setFieldValue("bio", value)}
          />
          {errors.bio && <FieldError>{errors.bio}</FieldError>}
        </div>
        <div className="md:col-span-2">
          {values.cv_path && (
            <p className="mb-2 text-sm">
              Current CV:{" "}
              <a
                href={cvPreview || values.cv_path}
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 underline"
              >
                View file
              </a>
            </p>
          )}
          <DocumentUpload
            title={lawyer ? "Replace CV" : "Upload CV"}
            name="lawyer_cv"
            label="Upload a PDF, DOC, or DOCX file"
            acceptedFileTypes={[
              "application/pdf",
              "application/msword",
              "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
            ]}
            maxFiles={1}
            onChange={onCvChange}
            required={!values.cv_path}
          />
          {errors.cv_path && <FieldError>{errors.cv_path}</FieldError>}
        </div>
        <SelectOption
          items={statusOptions}
          label="Application status"
          placeholder="Select application status"
          value={values.status}
          onChange={(status) => setFieldValue("status", status)}
        />
      </div>
    </Modal>
  );
};

const FieldError = ({ children }: { children: React.ReactNode }) => (
  <p className="mt-1 text-sm text-red-600" role="alert">
    {children}
  </p>
);

export default LawyerFormModal;
